/**
 * canvas-node-helpers.ts — CanvasNode 工具函数
 * 统一节点模型的查询、过滤、分类便捷函数
 */

import type { CanvasNode, SequenceClip, SequenceData } from '@/types/visual-editor';
import { isColorAdjustDefault, normalizeColorAdjust } from './color-adjust';
import { getNodeGenerationSourceImageUrl } from '@/lib/image-utils';
import { toDisplayMediaUrl } from '@/lib/media-url';
import { getDisplayThumbnailUrl } from '@/lib/task-media';

const MIN_SEQUENCE_CLIP_DURATION_MS = 500;
const MIN_SEQUENCE_PLAYBACK_RATE = 0.25;
const MAX_SEQUENCE_PLAYBACK_RATE = 4;

function clampSequencePlaybackRate(rate: number | null | undefined): number {
  if (!Number.isFinite(rate) || !rate) {
    return 1;
  }
  return Math.max(MIN_SEQUENCE_PLAYBACK_RATE, Math.min(MAX_SEQUENCE_PLAYBACK_RATE, rate));
}
type ExportableNode = Pick<
  CanvasNode,
  'mediaType' | 'thumbnail' | 'videoUrl' | 'modelUrl' | 'rotation' | 'flipH' | 'flipV' | 'colorAdj' | 'textOverlays' | 'stickerOverlays' | 'originalImageUrl' | 'thumbnailUrl'
>;

export type NodeExportPlan =
  | { kind: 'none' }
  | { kind: 'source-url'; url: string }
  | { kind: 'baked-image' };

export interface PreparedNodeExport {
  blob?: Blob;
  url?: string;
}

function normalizeSequenceResourceUrl(url?: string): string | undefined {
  if (typeof url !== 'string') {
    return undefined;
  }

  const normalized = url.trim();
  if (!normalized) {
    return undefined;
  }

  if (normalized.startsWith('blob:')) {
    return undefined;
  }

  if (normalized.startsWith('pending/') || normalized.includes('/pending/')) {
    return undefined;
  }

  return toDisplayMediaUrl(normalized);
}

function normalizeSequenceThumbnailUrl(url: string | undefined, mediaType: SequenceClip['mediaType']): string | undefined {
  const normalized = normalizeSequenceResourceUrl(url);
  if (!normalized) {
    return undefined;
  }

  return getDisplayThumbnailUrl(normalized, mediaType === 'video' ? 'video' : 'image');
}

function getSequenceSourceDurationMs(node?: CanvasNode): number | undefined {
  if (!node || node.mediaType !== 'video') {
    return undefined;
  }

  const rawDurationSec = Math.max(node.sourceDuration || 0, node.duration || 0);
  if (!(rawDurationSec > 0)) {
    return undefined;
  }

  return Math.round(rawDurationSec * 1000);
}

export function hydrateSequenceClip(clip: SequenceClip, sourceNode?: CanvasNode): SequenceClip {
  const selectedOutput = clip.outputs?.find((output) => output.id === clip.selectedOutputId)
    ?? clip.outputs?.[clip.outputs.length - 1];
  const cachedThumbnail = normalizeSequenceThumbnailUrl(clip.thumbnail, clip.mediaType);
  const cachedVideoUrl = normalizeSequenceResourceUrl(clip.videoUrl);
  const selectedOutputThumbnail = normalizeSequenceThumbnailUrl(selectedOutput?.thumbnail, clip.mediaType);
  const selectedOutputVideoUrl = normalizeSequenceResourceUrl(selectedOutput?.videoUrl);
  const sourceThumbnail = normalizeSequenceThumbnailUrl(sourceNode?.thumbnail, clip.mediaType);
  const sourceVideoUrl = normalizeSequenceResourceUrl(sourceNode?.videoUrl);
  const nextThumbnail = selectedOutputThumbnail || sourceThumbnail || cachedThumbnail;
  const nextVideoUrl = clip.mediaType === 'video'
    ? (selectedOutputVideoUrl || sourceVideoUrl || cachedVideoUrl)
    : undefined;

  let nextDuration = clip.duration;
  let nextTrimStart = clip.trimStart;
  let nextTrimEnd = clip.trimEnd;
  let nextSourceDuration = clip.sourceDuration;
  const playbackRate = clampSequencePlaybackRate(clip.playbackRate);

  if (clip.mediaType === 'video') {
    const sourceDurationMs = getSequenceSourceDurationMs(sourceNode);
    if (sourceDurationMs) {
      nextSourceDuration = sourceDurationMs;

      const currentTrimStart = Math.max(0, clip.trimStart ?? 0);
      const currentTrimEnd = clip.trimEnd ?? (currentTrimStart + clip.duration * playbackRate);
      const hasExplicitTrim = clip.trimStart !== undefined || clip.trimEnd !== undefined;
      const hasCustomSpeed = Math.abs(playbackRate - 1) > 0.001;
      const hasCustomTrim = hasExplicitTrim
        ? currentTrimStart > 0 || Math.abs(currentTrimEnd - sourceDurationMs) > 100
        : currentTrimStart > 0;

      if (hasCustomTrim || hasCustomSpeed) {
        const maxTrimStart = Math.max(0, sourceDurationMs - MIN_SEQUENCE_CLIP_DURATION_MS);
        const clampedTrimStart = Math.min(currentTrimStart, maxTrimStart);
        const clampedTrimEnd = Math.max(
          clampedTrimStart + MIN_SEQUENCE_CLIP_DURATION_MS,
          Math.min(sourceDurationMs, currentTrimEnd),
        );
        nextTrimStart = clampedTrimStart;
        nextTrimEnd = clampedTrimEnd;
        nextDuration = Math.max(
          MIN_SEQUENCE_CLIP_DURATION_MS,
          Math.round((clampedTrimEnd - clampedTrimStart) / playbackRate),
        );
      } else if (Math.abs(clip.duration - sourceDurationMs) > 100) {
        nextDuration = sourceDurationMs;
      }
    }
  }

  return {
    ...clip,
    duration: nextDuration,
    trimStart: nextTrimStart,
    trimEnd: nextTrimEnd,
    playbackRate,
    sourceDuration: nextSourceDuration,
    thumbnail: nextThumbnail,
    videoUrl: nextVideoUrl,
  };
}

export function hydrateSequenceData(sequenceData: SequenceData | undefined, nodes: CanvasNode[]): SequenceData | undefined {
  if (!sequenceData) {
    return undefined;
  }

  const nodeMap = new Map(nodes.map((node) => [node.id, node]));

  return {
    ...sequenceData,
    clips: sequenceData.clips.map((clip) => hydrateSequenceClip(clip, nodeMap.get(clip.sourceNodeId))),
  };
}

// ── 分类查询 ──

/** 是否是自由画布节点（非 prompt、非 sequence） */
export function isFloatingNode(node: CanvasNode): boolean {
  return node.mediaType !== 'prompt' && node.mediaType !== 'sequence';
}

/** 是否是 Prompt 节点 */
export function isPromptNode(node: CanvasNode): boolean {
  return node.mediaType === 'prompt';
}

/** 是否是 Sequence Area 节点 */
export function isSequenceNode(node: CanvasNode): boolean {
  return node.mediaType === 'sequence';
}

// ── 数组过滤（从 nodes[] 中取子集） ──

/** 获取自由画布节点（非 prompt、非 sequence） */
export function getFloatingNodes(nodes: CanvasNode[]): CanvasNode[] {
  return nodes.filter(isFloatingNode);
}

/** 获取 Prompt 节点 */
export function getPromptNodes(nodes: CanvasNode[]): CanvasNode[] {
  return nodes.filter(isPromptNode);
}

/** 获取 Sequence Area 节点 */
export function getSequenceNodes(nodes: CanvasNode[]): CanvasNode[] {
  return nodes.filter(isSequenceNode);
}

// ── 查找 ──

/** 在 nodes 中按 ID 查找（替代 shots.find || freeNodes.find） */
export function findNode(nodes: CanvasNode[], id: string): CanvasNode | undefined {
  return nodes.find(n => n.id === id);
}

/** 获取节点的主要媒体 URL */
export function getMediaUrl(node: CanvasNode): string | undefined {
  return node.replacedVideoUrl || node.videoUrl || node.modelUrl || node.thumbnail;
}

// ── 图片变换烘焙 ──

/** 节点是否有本地编辑变换（rotation/flip/colorAdj），即前端视觉 ≠ 实际图片文件 */
export function hasLocalTransforms(node: CanvasNode): boolean {
  if (node.stickerOverlays?.length) return true;
  if (node.textOverlays?.some(overlay => overlay.text.trim())) return true;
  if (node.rotation && node.rotation !== 0) return true;
  if (node.flipH) return true;
  if (node.flipV) return true;
  if (node.colorAdj && !isColorAdjustDefault(node.colorAdj)) return true;
  return false;
}

export function getNodeExportPlan(node: ExportableNode): NodeExportPlan {
  if (node.mediaType === 'video') {
    const videoUrl = normalizeSequenceResourceUrl(node.videoUrl);
    return videoUrl ? { kind: 'source-url', url: videoUrl } : { kind: 'none' };
  }

  if (node.mediaType === '3d_model') {
    const modelUrl = normalizeSequenceResourceUrl(node.modelUrl);
    return modelUrl ? { kind: 'source-url', url: modelUrl } : { kind: 'none' };
  }

  const thumbnail = getNodeGenerationSourceImageUrl(node as CanvasNode);
  if (!thumbnail) {
    return { kind: 'none' };
  }

  return hasLocalTransforms(node as CanvasNode)
    ? { kind: 'baked-image' }
    : { kind: 'source-url', url: thumbnail };
}

/**
 * 将节点的本地变换（rotation/flip/colorAdj）烘焙到图片中，
 * 返回变换后的 Blob。调用方自行上传获取 URL。
 *
 * - 仅处理图片节点（video/3d 返回 null）
 * - 无变换时返回 null（调用方应直接用 thumbnail）
 */
export async function bakeNodeTransforms(node: CanvasNode): Promise<Blob | null> {
  if (node.mediaType !== 'image' && node.mediaType !== undefined) {
    if (node.mediaType === 'video' || node.mediaType === '3d_model') return null;
  }
  if (!hasLocalTransforms(node)) return null;

  const url = getNodeGenerationSourceImageUrl(node);
  if (!url) return null;
  const { renderEditedSourceImageBlob } = await import('@/components/visual-editor/smartEditImageRender');
  return renderEditedSourceImageBlob(url, normalizeColorAdjust(node.colorAdj), {
    sourceWidth: 0, sourceHeight: 0, rotation: node.rotation ?? 0,
    flipH: !!node.flipH, flipV: !!node.flipV,
  }, node.textOverlays ?? [], { type: 'image/png', stickers: node.stickerOverlays });

}

export async function prepareNodeExport(node: CanvasNode): Promise<PreparedNodeExport | null> {
  const plan = getNodeExportPlan(node);

  if (plan.kind === 'none') {
    return null;
  }

  if (plan.kind === 'source-url') {
    return { url: plan.url };
  }

  const blob = await bakeNodeTransforms(node);
  if (!blob) {
    throw new Error(`Failed to bake local transforms for node ${node.id}`);
  }

  return { blob };
}
